/**
 * Run detail — a structured timeline, not a mirrored log.
 *
 * Events come from the backend's event stream (`RunTimeline`); raw prose
 * lives in the GitLab job trace and is linked, not copied (`RunReferences`
 * — the pipeline/job URLs are built server-side), keeping this page clear
 * of GitLab's ~100MB trace ceiling.
 *
 * `GET /agent-runs/{id}` 404s identically for an unknown id and for a run
 * this viewer may not see (neither theirs nor in a project they read).
 * `isNotFoundError` is the only place this page asks "was that a 404," and
 * the copy for it says only "not found" — it must never hint that a hidden
 * run might exist. A project reader may see another user's run but not stop
 * it, so Stop shows only when `can_stop`.
 *
 * `budget_seconds`/`budget_tokens` are LIMITS the run was allowed, not
 * what it consumed — nothing today tracks actual usage, so this must
 * never be labeled "cost" or "used".
 */

import type { ReactElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  DegradeBanner,
  RunReferences,
  RunResult,
  RunStatusBadge,
  RunUsageSummary,
  RunTimeline,
  StopRunControl,
  formatBudgetSeconds,
  isNotFoundError,
  useRun,
  useStopRun,
} from '@features/agent_runs';
import { ROUTES } from '@utils/constants';

export function RunDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const { data: run, isLoading, error } = useRun(id ?? '');
  const { mutateAsync: stop, isPending: isStopping, error: stopError } = useStopRun();

  const handleStop = async (): Promise<void> => {
    if (!id) return;
    await stop(id);
  };

  if (isLoading) {
    return (
      <div className="run-detail-page">
        <div className="loading-message">Loading run...</div>
      </div>
    );
  }

  // A failed refetch of a run already shown keeps it, with an inline alert:
  // a reader's GET re-checks their access live, so a GitLab or sign-in blip
  // answers 404 for one poll; "not found" is only for a run never loaded.
  if (error && !run) {
    // Deliberately the same copy whether the id never existed or the run
    // is hidden from this viewer — the backend makes those indistinguishable
    // on purpose, and this page must not undo that.
    const message = isNotFoundError(error)
      ? 'This run was not found.'
      : 'Failed to load this run.';
    return (
      <div className="run-detail-page">
        <div className="run-detail-page__message">
          <h2>{message}</h2>
          <Link to={ROUTES.RUNS} className="back-link">
            &larr; Back to Runs
          </Link>
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="run-detail-page">
        <div className="run-detail-page__message">
          <h2>This run was not found.</h2>
          <Link to={ROUTES.RUNS} className="back-link">
            &larr; Back to Runs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="run-detail-page">
      <div className="page-nav">
        <Link to={ROUTES.RUNS} className="back-link">
          &larr; Back to Runs
        </Link>
      </div>

      {error && (
        <div className="error-message" role="alert">
          Couldn&apos;t refresh this run; it will try again.
        </div>
      )}

      <div className="run-detail__header">
        <h2>
          {run.kind} <span className="run-detail__ref">on {run.ref}</span>
        </h2>
        <RunStatusBadge status={run.status} />
      </div>

      <dl className="run-detail__meta">
        <div>
          <dt>Identity</dt>
          <dd>{run.identity_mode === 'brokered' ? 'Brokered (your credential)' : 'Bot (read-only)'}</dd>
        </div>
        <div>
          <dt>Project</dt>
          <dd title={run.project_id}>{run.project_path ?? run.project_id.slice(0, 8)}</dd>
        </div>
        <div>
          <dt>Run id</dt>
          <dd>{run.id}</dd>
        </div>
        <div>
          <dt>Time budget</dt>
          <dd>{formatBudgetSeconds(run.budget_seconds)}</dd>
        </div>
        <div>
          <dt>Model usage</dt>
          <RunUsageSummary usage={run.usage} budgetTokens={run.budget_tokens} />
        </div>
      </dl>

      <DegradeBanner reason={run.degraded_reason} />

      <RunResult identityMode={run.identity_mode} result={run.result} mrUrl={run.mr_url} />

      <RunReferences
        pipelineId={run.pipeline_id}
        pipelineUrl={run.pipeline_url}
        jobId={run.job_id}
        jobUrl={run.job_url}
      />

      <StopRunControl
        status={run.status}
        canStop={run.can_stop}
        onStop={() => { void handleStop(); }}
        isStopping={isStopping}
      />
      {stopError && <div className="error-message">{stopError.message}</div>}

      <h2 className="run-detail__timeline-title">Timeline</h2>
      <RunTimeline events={run.events} />
    </div>
  );
}
